import { Controller } from "@hotwired/stimulus";

type SaveEvent = CustomEvent<{
  elt: HTMLElement;
  xhr: XMLHttpRequest;
  successful?: boolean;
  failed?: boolean;
}>;

/** Observe the existing HTMX writes without taking ownership of persistence. */
export default class extends Controller<HTMLFormElement> {
  static targets = ["status", "retry"];
  static values = { saved: String, saving: String, unsaved: String, failed: String };
  declare readonly statusTarget: HTMLElement;
  declare readonly retryTargets: HTMLButtonElement[];
  declare readonly savedValue: string;
  declare readonly savingValue: string;
  declare readonly unsavedValue: string;
  declare readonly failedValue: string;

  private saved = "";
  private requests = new WeakMap<XMLHttpRequest, string>();
  private requester: HTMLElement | null = null;

  connect(): void {
    if (!this.element.classList.contains("hidden")) this.saved = this.snapshot();
  }

  open(): void {
    this.saved = this.snapshot();
  }

  changed(event?: Event): void {
    // Rich editors emit a second input from their synchronized form field.
    if (event?.type === "input" && event.target instanceof HTMLElement && event.target.isContentEditable) return;
    this.statusTarget.textContent = this.snapshot() === this.saved ? this.savedValue : this.unsavedValue;
    this.statusTarget.classList.remove("text-destructive");
  }

  started(event: SaveEvent): void {
    this.requester = event.detail.elt;
    this.requests.set(event.detail.xhr, this.snapshot());
    this.statusTarget.textContent = this.savingValue;
    this.statusTarget.classList.remove("text-destructive");
    for (const retry of this.retryTargets) retry.hidden = true;
  }

  finished(event: SaveEvent): void {
    if (!event.detail.successful || event.detail.failed) {
      this.failed();
      if (this.element.matches('[data-controller~="email-autosave"]') && [409, 422].includes(event.detail.xhr.status)
          && event.detail.xhr.getResponseHeader("Content-Type")?.startsWith("text/plain")) {
        this.statusTarget.textContent = event.detail.xhr.responseText;
      }
      return;
    }
    const saved = this.requests.get(event.detail.xhr);
    if (saved === undefined) return;
    this.saved = saved;
    for (const retry of this.retryTargets) retry.hidden = true;
    this.changed();
  }

  failed(event?: CustomEvent<{ message?: string }>): void {
    this.statusTarget.textContent = event?.detail?.message || this.failedValue;
    this.statusTarget.classList.add("text-destructive");
    for (const retry of this.retryTargets) retry.hidden = false;
  }

  retry(): void {
    if (this.requester?.isConnected) {
      this.requester.dispatchEvent(new Event("retry-save", { bubbles: true }));
    }
  }

  private snapshot(): string {
    return JSON.stringify(Array.from(new FormData(this.element).entries()).filter(([name]) => name !== "emailDraftToken"));
  }
}
